import type { RefObject } from 'react';
import { useMemo, useState, useRef, useLayoutEffect } from 'react';

type Rect = {
  width: number;
  height: number;
  top: number;
  left: number;
  bottom: number;
  right: number;
  x: number;
  y: number;
};

const initialRect: Rect = {
  width: 0,
  height: 0,
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  x: 0,
  y: 0,
};

export const useElementObserver = <T extends HTMLElement>(): [RefObject<T | null>, Rect] => {
  const ref = useRef<T | null>(null);
  const [rect, setRect] = useState<Rect>(initialRect);
  const observer = useMemo(
    () =>
      typeof document === 'undefined'
        ? null
        : new ResizeObserver((entries) => {
            if (entries[0]) {
              const { width, height, top, left, bottom, right, x, y } = entries[0].contentRect;
              setRect({ width, height, top, left, bottom, right, x, y });
            }
          }),
    [],
  );

  useLayoutEffect(() => {
    if (!ref.current || !observer) {
      return;
    }

    observer.observe(ref.current);

    // eslint-disable-next-line consistent-return
    return () => {
      observer.disconnect();
    };
  }, [observer]);

  return [ref, rect];
};
